import React from "react";

import { PayoutDialog } from "features/Payments/components/PayoutDialog";
import { PayoutsTable } from "features/Payments/components/PayoutsTable";
import { usePartnerPayouts } from "features/Payments/hooks/usePartnerPayouts";
import { MutedLine, TotalAmount, TotalCard, TotalLabel, Totals } from "features/Payments/styles";
import { PartnerDto } from "services/partners/restModels";
import { formatDate } from "shared/lib/dateTime";
import { formatPrice } from "shared/lib/price";
import { Button } from "shared/ui/Button";
import { ConfirmDialog } from "shared/ui/ConfirmDialog";
import { EmptyState } from "shared/ui/EmptyState";
import { UseNotificationSnackbarReturn } from "shared/ui/NotificationSnackbar/useNotificationSnackbar";
import { Skeleton } from "shared/ui/Skeleton";

import { ALL_TIME_FROM, ALL_TIME_TO, DEFAULT_CURRENCY_ID } from "../../constants";
import { PayoutActions } from "../../styles";

interface PartnerPayoutsTabProps {
    partner: PartnerDto;
    notify: UseNotificationSnackbarReturn;
}

export const PartnerPayoutsTab = ({ partner, notify }: PartnerPayoutsTabProps) => {
    const payouts = usePartnerPayouts(ALL_TIME_FROM, ALL_TIME_TO, partner.id, notify);

    if (!payouts.payouts) return <Skeleton height="240px" />;

    const { items, totals } = payouts.payouts;

    return (
        <>
            <PayoutActions>
                <Button onClick={payouts.onStartCreating}>Зафиксировать выплату</Button>
            </PayoutActions>

            {totals.map(({ currencyId, paidAmount, remainingAmount }) => (
                <Totals key={currencyId}>
                    <TotalCard>
                        <TotalLabel>Начислено</TotalLabel>
                        <TotalAmount>{formatPrice(paidAmount + remainingAmount, currencyId)}</TotalAmount>
                        <MutedLine>За всё время, по текущему проценту</MutedLine>
                    </TotalCard>

                    <TotalCard>
                        <TotalLabel>Выплачено</TotalLabel>
                        <TotalAmount>{formatPrice(paidAmount, currencyId)}</TotalAmount>
                        <MutedLine>За всё время</MutedLine>
                    </TotalCard>

                    <TotalCard $accent>
                        <TotalLabel>Остаток к выплате</TotalLabel>
                        <TotalAmount>{formatPrice(remainingAmount, currencyId)}</TotalAmount>
                        <MutedLine>Начислено минус выплачено</MutedLine>
                    </TotalCard>
                </Totals>
            ))}

            {items.length === 0 ? (
                <EmptyState title="Выплат пока не было" />
            ) : (
                <PayoutsTable
                    items={items}
                    canManage
                    showPartner={false}
                    onDelete={payouts.onStartDeleting}
                />
            )}

            {payouts.isCreating && (
                <PayoutDialog
                    partnerId={partner.id}
                    partnerName={partner.name}
                    currencies={totals.length > 0 ? totals.map(({ currencyId }) => currencyId) : [DEFAULT_CURRENCY_ID]}
                    saving={payouts.saving}
                    onClose={payouts.onCancelCreating}
                    onSubmit={payouts.onCreate}
                />
            )}

            {payouts.deleting && (
                <ConfirmDialog
                    open
                    title="Удалить выплату?"
                    description={`Запись на ${formatPrice(
                        payouts.deleting.amount,
                        payouts.deleting.currencyId
                    )} от ${formatDate(payouts.deleting.paidAt)} исчезнет, остаток к выплате вырастет.`}
                    confirmLabel="Удалить"
                    tone="danger"
                    saving={payouts.saving}
                    onClose={payouts.onCancelDeleting}
                    onConfirm={payouts.onConfirmDeleting}
                />
            )}
        </>
    );
};
